import { useMutation, useQuery } from '@tanstack/react-query';
import { AxiosInstance } from 'axios';
import { useAxiosClientsContextProvider } from 'shared/axios/axios-clients-provider';

import CreateIS32DeviceCommand from 'features/systems/IS32/domain/models/create-IS32-device-command';
import CreateIS32DeviceResponse from 'features/systems/IS32/domain/models/create-IS32-device-response';
import DeleteIS32RoomUnitCommand from 'features/systems/IS32/domain/models/delete-IS32-room-unit-command';
import { IS32ConfigurationUnit } from 'features/systems/IS32/domain/models/IS32-configuration-unit';
import UpdateIS32ConfigurationCommand from 'features/systems/IS32/domain/models/update-IS32-configuration-command';
import { ReadIS32ConfigurationResponse } from '../models/read-IS32-configuration-unit-response';

const IS32_BASE_URL = '/api/v1/ISThirtyTwo';

export enum IS32QueryPaths {
  ScanConfiguration = 'ScanConfiguration',
  GetIS32Configuration = 'ReadISThirtyTwoConfiguration',
  UpdateIS32Configuration = 'UpdateISThirtyTwoConfiguration',
  CreateIS32Device = 'CreateISThirthyTwoDevice',
  DeleteIS32RoomUnit = 'DeleteISThirtyTwoRoomUnit',
}

const scanConfiguration = async (axiosInstance: AxiosInstance, systemId: string): Promise<void> => {
  await axiosInstance.get(`${IS32_BASE_URL}/${IS32QueryPaths.ScanConfiguration}/${systemId}`);
};

const getIS32Configuration = async (
  axiosInstance: AxiosInstance,
  systemId: string,
  signal?: AbortSignal,
): Promise<ReadIS32ConfigurationResponse> => {
  const response = await axiosInstance.get(`${IS32_BASE_URL}/${IS32QueryPaths.GetIS32Configuration}/${systemId}`, { signal });
  return response.data;
};

const updateIS32Configuration = async (
  axiosInstance: AxiosInstance,
  body: UpdateIS32ConfigurationCommand,
): Promise<IS32ConfigurationUnit> => {
  const response = await axiosInstance.put(`${IS32_BASE_URL}/${IS32QueryPaths.UpdateIS32Configuration}`, body);
  return response.data;
};

const createIS32Device = async (axiosInstance: AxiosInstance, body: CreateIS32DeviceCommand): Promise<CreateIS32DeviceResponse> => {
  const response = await axiosInstance.post(`${IS32_BASE_URL}/${IS32QueryPaths.CreateIS32Device}`, body);
  return response.data;
};

const deleteIS32RoomUnit = async (axiosInstance: AxiosInstance, command: DeleteIS32RoomUnitCommand): Promise<void> => {
  const { systemId, address } = command;
  await axiosInstance.delete(`${IS32_BASE_URL}/${IS32QueryPaths.DeleteIS32RoomUnit}/${systemId}/${address}`);
};

export const useScanConfiguration = () => {
  const { authenticatedInstance } = useAxiosClientsContextProvider();
  return useMutation({
    mutationKey: [IS32QueryPaths.ScanConfiguration],
    mutationFn: (systemId: string) => scanConfiguration(authenticatedInstance, systemId),
  });
};

export const useLazyGetIS32Configuration = (systemId: string) => {
  const { authenticatedInstance } = useAxiosClientsContextProvider();
  return useQuery({
    queryKey: [IS32QueryPaths.GetIS32Configuration],
    queryFn: ({ signal }) => getIS32Configuration(authenticatedInstance, systemId, signal),
    enabled: false,
  });
};

export const useUpdateIS32Configuration = () => {
  const { authenticatedInstance } = useAxiosClientsContextProvider();
  return useMutation({
    mutationKey: [IS32QueryPaths.UpdateIS32Configuration],
    mutationFn: (body: UpdateIS32ConfigurationCommand) => updateIS32Configuration(authenticatedInstance, body),
  });
};

export const useCreateIS32Device = () => {
  const { authenticatedInstance } = useAxiosClientsContextProvider();
  return useMutation({
    mutationKey: [IS32QueryPaths.CreateIS32Device],
    mutationFn: (body: CreateIS32DeviceCommand) => createIS32Device(authenticatedInstance, body),
  });
};

export const useDeleteIS32RoomUnit = () => {
  const { authenticatedInstance } = useAxiosClientsContextProvider();
  return useMutation({
    mutationKey: [IS32QueryPaths.DeleteIS32RoomUnit],
    mutationFn: (command: DeleteIS32RoomUnitCommand) => deleteIS32RoomUnit(authenticatedInstance, command),
  });
};
